import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { issueInvoiceSchema, type InvoiceDetailDto, type IssueInvoiceInput } from "@tc/shared";
import { Button, Field, Modal, Tag, Textarea, TextInput, Toast, type TagVariant } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { ApiError } from "@/features/trips/api";
import { recordsApi, runRecordPdf } from "./api";
import { FacturaLineEditor } from "./FacturaLineEditor";
import { FacturaView } from "./FacturaView";

const STATUS_TAG: Record<InvoiceDetailDto["status"], { variant: TagVariant; key: string }> = {
  Draft: { variant: "neutral", key: "invoice.draft" },
  Issued: { variant: "primary", key: "records.issued" },
  Paid: { variant: "success", key: "records.paid" },
};

/**
 * Factura page (TW-3527) — the visual invoice template, reachable from any invoice row at
 * `/trips/:id/invoices/:invoiceId`. Both audiences can view (a club only ever reaches an Issued/Paid
 * one — the invoice RLS Draft predicate 404s a Draft for the club). Back office can edit a Draft's
 * line items inline and issue it; once Issued/Paid both audiences can download the branded PDF.
 * Issuing goes through a modal (TRAV-2): the invoice-issued email's recipients (pre-filled with the
 * club's accounting email, comma-separated for several) + an optional personal message. Beside the
 * Issue button the page previews the number the invoice will receive (informational — allocation
 * stays atomic at issue) with a quick link to the numbering settings.
 */
export function FacturaPage() {
  const { t } = useTranslation();
  const { id: tripId, invoiceId } = useParams<{ id: string; invoiceId: string }>();
  const { isBackOffice, has } = useAuthz();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<InvoiceDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [nextNumber, setNextNumber] = useState<string | null>(null);

  // Issue modal state (TRAV-2).
  const [issueOpen, setIssueOpen] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [issueModalError, setIssueModalError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!tripId || !invoiceId) return;
    recordsApi
      .getInvoiceDetail(tripId, invoiceId)
      .then(setDetail)
      .catch(() => setError(t("factura.loadError")));
  }, [tripId, invoiceId, t]);

  useEffect(() => load(), [load]);

  const isDraft = detail?.status === "Draft";

  // The informational next-number preview, shown beside the Issue button on a Draft (BO-only).
  useEffect(() => {
    if (!isBackOffice || !isDraft) return;
    let cancelled = false;
    recordsApi
      .nextInvoiceNumber()
      .then((r) => {
        if (!cancelled) setNextNumber(r.number);
      })
      .catch(() => undefined); // purely informational — never block the page on it
    return () => {
      cancelled = true;
    };
  }, [isBackOffice, isDraft]);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  }

  /** Open the issue modal, pre-filled with the club's accounting email (TRAV-2). */
  function openIssueModal() {
    setRecipients(detail?.accountingEmail ?? "");
    setMessage("");
    setIssueModalError(null);
    setIssueOpen(true);
  }

  async function confirmIssue() {
    if (!tripId || !invoiceId) return;
    // One shared schema (zod, @tc/shared) validates here and again server-side.
    const input: IssueInvoiceInput = {
      recipients: recipients
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      message: message.trim() || undefined,
    };
    const parsed = issueInvoiceSchema.safeParse(input);
    if (!parsed.success) {
      setIssueModalError(t("factura.issueModal.invalidEmail"));
      return;
    }
    setBusy(true);
    setIssueModalError(null);
    try {
      const updated = await recordsApi.issueInvoice(tripId, invoiceId, parsed.data);
      setDetail((d) => (d ? { ...d, ...updated } : d));
      setIssueOpen(false);
      load();
      flash(t("factura.issued"));
    } catch (err) {
      setIssueModalError(err instanceof ApiError ? err.message : t("factura.issueError"));
    } finally {
      setBusy(false);
    }
  }

  async function markPaid() {
    if (!tripId || !invoiceId) return;
    setBusy(true);
    try {
      await recordsApi.payInvoice(tripId, invoiceId);
      load();
      flash(t("factura.markedPaid"));
    } catch {
      flash(t("factura.actionError"));
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf() {
    if (!tripId || !invoiceId) return;
    setBusy(true);
    try {
      await runRecordPdf(
        () => recordsApi.requestInvoicePdf(tripId, invoiceId),
        (eid) => recordsApi.invoicePdfStatus(tripId, invoiceId, eid),
      );
    } catch {
      flash(t("factura.pdfError"));
    } finally {
      setBusy(false);
    }
  }

  if (error) return <div className="page"><p className="muted">{error}</p></div>;
  if (!detail) return <div className="page"><p className="muted">{t("common.loading")}</p></div>;

  const canEdit = isBackOffice && isDraft;
  const status = STATUS_TAG[detail.status];

  return (
    <div className="page">
      <Button
        variant="ghost"
        size="sm"
        icon="chevL"
        style={{ marginBottom: 10, paddingLeft: 4 }}
        onClick={() => navigate(`/trips/${tripId}`)}
      >
        {t("factura.back")}
      </Button>

      <div className="row-between page-head" style={{ alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 style={{ margin: 0 }}>{detail.number}</h1>
          <Tag variant={status.variant}>{t(status.key)}</Tag>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {isBackOffice && isDraft && nextNumber && (
            <span className="fac-next-note">
              {t("factura.nextNumber", { number: nextNumber })}
              {/* The invoicing-settings section is `invoice.settings`-gated — only link there when
                  this operator can actually open it (else SettingsPage bounces to profile). */}
              {has("invoice.settings") && (
                <>
                  {" "}
                  <Link to="/settings/invoicing">{t("factura.editNumbering")}</Link>
                </>
              )}
            </span>
          )}
          {isBackOffice && isDraft && (
            <Button variant="primary" icon="check" onClick={openIssueModal} disabled={busy}>
              {t("factura.issue")}
            </Button>
          )}
          {isBackOffice && detail.status === "Issued" && (
            <Button variant="secondary" onClick={markPaid} disabled={busy}>
              {t("factura.markPaid")}
            </Button>
          )}
          <Button variant="ghost" icon="download" onClick={downloadPdf} disabled={busy}>
            {isDraft ? t("factura.downloadProforma") : t("factura.downloadPdf")}
          </Button>
        </div>
      </div>

      {isDraft && (
        <p className="muted" style={{ marginTop: -6, marginBottom: 16 }}>
          {t("factura.proformaBanner")}
        </p>
      )}

      {canEdit && <FacturaLineEditor invoice={detail} onSaved={(d) => setDetail(d)} />}

      <FacturaView invoice={detail} />

      <Modal open={issueOpen} onClose={() => setIssueOpen(false)} title={t("factura.issueModal.title")} width={520}>
        <div className="modal-body">
          <div className="admin-form">
            <Field label={t("factura.issueModal.recipients")} hint={t("factura.issueModal.recipientsHint")}>
              <TextInput
                value={recipients}
                onChange={setRecipients}
                placeholder={t("factura.issueModal.recipientsPlaceholder")}
                inputMode="email"
              />
            </Field>
            <Field label={t("factura.issueModal.message")}>
              <Textarea
                value={message}
                onChange={setMessage}
                rows={4}
                placeholder={t("factura.issueModal.messagePlaceholder")}
              />
            </Field>
            {issueModalError && (
              <span className="foot-note" style={{ color: "var(--danger)" }} role="alert">
                {issueModalError}
              </span>
            )}
          </div>
        </div>
        <div className="modal-foot">
          <Button type="button" variant="ghost" onClick={() => setIssueOpen(false)}>
            {t("actions.cancel")}
          </Button>
          <Button type="button" variant="primary" icon="check" onClick={confirmIssue} disabled={busy}>
            {t("factura.issueModal.confirm")}
          </Button>
        </div>
      </Modal>

      {toast && <Toast message={toast} />}
    </div>
  );
}
